/**
 * approvalCopy — plain-English strings for the Approvals page.
 *
 * Founders should never have to read `klaviyo.send_campaign` or a raw JSON
 * `execution_result` to know what a card means. These pure functions turn the
 * stored action/result into one honest sentence; the raw shape stays reachable
 * behind a "technical details" disclosure in the UI. Kept dependency-free so it
 * is unit-tested directly (tests/approval-copy.test.ts).
 */

/** Pull the action's `type`/`kind` out of its `unknown` shape. */
export function actionType(action: unknown): string | null {
  if (typeof action === 'string') return action.trim() || null;
  if (action && typeof action === 'object') {
    const a = action as Record<string, unknown>;
    const t = a.type ?? a.kind;
    if (typeof t === 'string' && t.trim()) return t.trim();
  }
  return null;
}

/**
 * "What happens if I approve" — plain English for the action about to run.
 * (The raw action stays reachable behind the technical-details disclosure.)
 */
export function humanizeAction(action: unknown): string {
  const type = actionType(action);
  if (type === 'klaviyo.send_campaign')
    return 'Sends this email in Klaviyo to the founders-only seed list (3 people). Customers never receive it.';
  if (type === 'noop.echo') return 'Safe test action — nothing real happens.';
  if (type) return `Runs the stored action: ${type} (sandbox-guarded)`;
  return 'Runs the stored action (sandbox-guarded).';
}

/** Compact one-liner for an unknown result — the honest fallback. */
export function summarizeResult(result: unknown): string {
  if (result == null) return 'no result recorded';
  if (typeof result === 'string') return result;
  try {
    const s = JSON.stringify(result);
    return s.length > 140 ? `${s.slice(0, 137)}…` : s;
  } catch {
    return String(result);
  }
}

/** True when a result carries a non-empty `error` — i.e. execution failed. */
function resultError(result: unknown): string | null {
  if (result && typeof result === 'object') {
    const e = (result as Record<string, unknown>).error;
    if (typeof e === 'string' && e.trim()) return e.trim();
  }
  return null;
}

export interface ExecutionCopy {
  /** One plain-English sentence describing what actually ran. */
  text: string;
  /** The action failed — the UI renders this in coral. */
  failed: boolean;
}

/**
 * "What actually happened" — a founder-readable receipt for a decided,
 * executed approval, paired from the action type + the stored result. Anything
 * unrecognised falls back to the honest compact summary rather than pretending.
 */
export function humanizeResult(action: unknown, result: unknown): ExecutionCopy {
  const err = resultError(result);
  if (err) return { text: `Didn't go through — ${err}`, failed: true };

  const type = actionType(action);
  if (type === 'klaviyo.send_campaign')
    return {
      text: 'Email sent in Klaviyo to the founders-only seed list (3 people). No customer received it.',
      failed: false,
    };
  if (type === 'noop.echo')
    return { text: 'Safe test action ran — nothing real happened.', failed: false };

  if (result == null) return { text: 'Ran — no result recorded.', failed: false };
  return { text: `Ran — ${summarizeResult(result)}`, failed: false };
}

/** The minimum shape of a Quinn report this helper needs (kept structural so
 *  approvalCopy stays dependency-free and unit-testable in isolation). */
type QuinnLike = { passed: boolean; checks?: Array<{ ok: boolean }> } | null | undefined;

/** Chip tone → the existing `chip-*` classes on the Approvals page. */
export type QuinnTone = 'mint' | 'coral' | 'dim';

export interface QuinnStatus {
  /** One-glance label a non-technical founder can trust. */
  label: string;
  /** Chip colour: mint = passed, coral = flagged issues, dim = not reviewed yet. */
  tone: QuinnTone;
  /** Quinn actually ran her walkthrough (a report exists). */
  reviewed: boolean;
}

/**
 * Quinn's QA verdict for a PENDING approval, as a founder-readable chip.
 *
 * The old UI collapsed two very different states into one ambiguous "Not QA'd":
 *   • no report            → nobody has checked this yet
 *   • report, passed:false → Quinn DID review and FOUND PROBLEMS
 * A founder reading "Not QA'd" on the second could approve flagged work thinking
 * it was merely untested. These are now told apart honestly:
 *   • no report      → "Awaiting Quinn's review" (dim, reviewed:false)
 *   • passed         → "Quinn passed" (mint)
 *   • found issues   → "Quinn flagged N issue(s)" (coral) — she looked, and objects
 */
export function quinnStatus(report: QuinnLike): QuinnStatus {
  if (!report) return { label: "Awaiting Quinn's review", tone: 'dim', reviewed: false };
  if (report.passed) return { label: 'Quinn passed', tone: 'mint', reviewed: true };
  const issues = (report.checks ?? []).filter((c) => !c.ok).length;
  const label =
    issues > 0 ? `Quinn flagged ${issues} issue${issues === 1 ? '' : 's'}` : 'Quinn flagged issues';
  return { label, tone: 'coral', reviewed: true };
}
